import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Autocomplete,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Chip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getSOWById,
  updateSOWDetails,
  linkProjectsToSOW,
  getLinkedProjectsBySOWId,
} from "../../http/requests/sow";
import SOWResourcingDetailsModal from "../SOWResourcingDetailsModal/SOWResourcingDetailsModal";
import { getProjectAdminDetails } from "../../http/requests/project-admin";
import { getEmployeeDropdownData } from "../../http/requests/session";
import { getAllClients, getListItems } from "../../http/requests/dropdown";
import { getSOWData } from "../../redux/actions";
import { toastOptions } from "../../common/utils/toasterOptions";

const projectId = (p) => p?.projectId ?? p?.ProjectId;
const projectName = (p) => p?.projectName ?? p?.ProjectName ?? "";
const clientName = (p) => p?.clientName ?? p?.ClientName ?? "";
const projectLabel = (p) => `${projectName(p)} (${clientName(p)})`;
const empId = (e) => e?.azureId ?? e?.AzureId ?? "";
const empLabel = (e) => e?.name ?? e?.Name ?? e?.email ?? e?.Email ?? "";
const clientId = (c) => c?.id ?? c?.Id;
const clientLabel = (c) => c?.name ?? c?.Name ?? "";
const itemId = (o) => o?.id ?? o?.Id;
const shortCode = (o) => o?.shortCodeValue ?? o?.ShortCodeValue ?? "";
const longCode = (o) => o?.longCodeValue ?? o?.LongCodeValue ?? "";

const formatDate = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.split("T")[0] || d;
  return d;
};

const SOWEditModal = ({ open, onClose, sowRow, onSuccess }) => {
  const dispatch = useDispatch();
  const sowListClientFilterId = useSelector((s) => s.MODULES.sowListClientFilterId);
  const sowId = sowRow?.sowId ?? sowRow?.SOWId;

  const [loadingSOW, setLoadingSOW] = useState(false);
  const [sow, setSow] = useState(null);
  const [formData, setFormData] = useState(null);
  const [clients, setClients] = useState([]);
  const [sowTypeOptions, setSowTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [updatingSOW, setUpdatingSOW] = useState(false);

  const [allProjects, setAllProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [updatingLinks, setUpdatingLinks] = useState(false);
  const [monthlyProjectionOpen, setMonthlyProjectionOpen] = useState(false);

  useEffect(() => {
    if (!open || !sowId) return;
    const load = async () => {
      setLoadingSOW(true);
      try {
        const [sowRes, clientsRes, typeRes, statusRes, employeesRes, projectsRes] = await Promise.all([
          getSOWById(sowId),
          getAllClients(),
          getListItems("projectType"),
          getListItems("projectStatus"),
          getEmployeeDropdownData(),
          getProjectAdminDetails(1, 500, "ASC", "projectName", "", false),
        ]);
        const sowData = sowRes?.data ?? sowRes;
        setSow(sowData);
        setFormData({
          sowName: sowData?.sowName ?? sowData?.SOWName ?? "",
          clientId: sowData?.clientId ?? sowData?.ClientId ?? "",
          sowStartDate: sowData?.sowStartDate ?? sowData?.SOWStartDate ? dayjs(formatDate(sowData.sowStartDate ?? sowData.SOWStartDate)) : null,
          sowEndDate: sowData?.sowEndDate ?? sowData?.SOWEndDate ? dayjs(formatDate(sowData.sowEndDate ?? sowData.SOWEndDate)) : null,
          sowType: sowData?.sowType ?? sowData?.SOWType ?? "",
          status: sowData?.status ?? sowData?.Status ?? "",
          deliveryManagerUserId: sowData?.deliveryManagerUserId ?? sowData?.DeliveryManagerUserId ?? "",
          accountManagerUserID: sowData?.accountManagerUserID ?? sowData?.AccountManagerUserID ?? "",
          resourceCount: (sowData?.resourceCount ?? sowData?.ResourceCount) != null ? String(sowData.resourceCount ?? sowData.ResourceCount) : "",
          namedSOWFlag: sowData?.namedSOWFlag ?? sowData?.NamedSOWFlag ?? false,
          note: sowData?.note ?? sowData?.Note ?? "",
        });
        setClients(Array.isArray(clientsRes) ? clientsRes : []);
        setSowTypeOptions(Array.isArray(typeRes) ? typeRes : []);
        setStatusOptions(Array.isArray(statusRes) ? statusRes : []);
        setEmployees(Array.isArray(employeesRes) ? employeesRes : employeesRes?.data ?? employeesRes?.Data ?? []);
        setAllProjects(Array.isArray(projectsRes) ? projectsRes : projectsRes?.data ?? projectsRes?.Data ?? []);
      } catch (err) {
        console.error("Failed to load SOW:", err);
        toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to load SOW", toastOptions);
      } finally {
        setLoadingSOW(false);
      }
    };
    load();
  }, [open, sowId]);

  useEffect(() => {
    if (!open || !sowId || allProjects.length === 0) return;
    const load = async () => {
      setLoadingLinked(true);
      try {
        const ids = await getLinkedProjectsBySOWId(sowId);
        const idList = Array.isArray(ids) ? ids : [];
        setSelectedProjects(allProjects.filter((p) => idList.includes(projectId(p))));
      } catch (err) {
        console.error("Failed to load linked projects:", err);
      } finally {
        setLoadingLinked(false);
      }
    };
    load();
  }, [open, sowId, allProjects]);

  const handleClose = () => {
    setFormData(null);
    setSow(null);
    setSelectedProjects([]);
    onClose();
  };

  const handleSOWChange = (field, value) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleUpdateSOWInfo = async () => {
    if (!formData || !sowId) return;
    if (!formData.sowName?.trim()) {
      toast.error("SOW Name is required", toastOptions);
      return;
    }
    const clientIdNum = typeof formData.clientId === "number" ? formData.clientId : parseInt(formData.clientId, 10);
    if (!formData.clientId || Number.isNaN(clientIdNum) || clientIdNum <= 0) {
      toast.error("Client is required", toastOptions);
      return;
    }
    if (!formData.sowStartDate || !formData.sowEndDate) {
      toast.error("Start and End dates are required", toastOptions);
      return;
    }
    if (!formData.sowType) {
      toast.error("SOW Type is required", toastOptions);
      return;
    }
    const start = dayjs(formData.sowStartDate);
    const end = dayjs(formData.sowEndDate);
    if (end.isBefore(start)) {
      toast.error("End Date must be on or after Start Date", toastOptions);
      return;
    }
    setUpdatingSOW(true);
    try {
      const resourceCountVal = formData.resourceCount !== "" && formData.resourceCount != null && !Number.isNaN(Number(formData.resourceCount)) ? Number(formData.resourceCount) : null;
      await updateSOWDetails({
        sowId,
        sowName: formData.sowName.trim(),
        clientId: clientIdNum,
        sowStartDate: start.format("YYYY-MM-DD"),
        sowEndDate: end.format("YYYY-MM-DD"),
        sowType: formData.sowType,
        resourceCount: resourceCountVal,
        namedSOWFlag: formData.namedSOWFlag,
        status: formData.status?.trim() || null,
        deliveryManagerUserId: formData.deliveryManagerUserId?.trim() || null,
        accountManagerUserID: formData.accountManagerUserID?.trim() || null,
        note: formData.note?.trim() || null,
        active: true,
      });
      toast.success("SOW updated successfully", toastOptions);
      dispatch(getSOWData({ pageNo: 1, pageSize: 10, sortBy: "sowName", sortDir: "ASC", searchData: "", clientId: sowListClientFilterId }));
      setSow((prev) => (prev ? { ...prev, ...formData, sowStartDate: start.format("YYYY-MM-DD"), sowEndDate: end.format("YYYY-MM-DD") } : null));
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to update SOW", toastOptions);
    } finally {
      setUpdatingSOW(false);
    }
  };

  const handleUpdateLinkedProjects = async () => {
    const ids = [...new Set(selectedProjects.map((p) => projectId(p)).filter(Boolean))];
    if (ids.length === 0) {
      toast.error("Select at least one project", toastOptions);
      return;
    }
    setUpdatingLinks(true);
    try {
      await linkProjectsToSOW(sowId, ids);
      toast.success("Linked projects updated", toastOptions);
      dispatch(getSOWData({ pageNo: 1, pageSize: 10, sortBy: "sowName", sortDir: "ASC", searchData: "", clientId: sowListClientFilterId }));
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to update linked projects", toastOptions);
    } finally {
      setUpdatingLinks(false);
    }
  };

  const filterProjectOptions = (options, { inputValue }) => {
    const q = (inputValue || "").toLowerCase().trim();
    if (!q) return options;
    return options.filter(
      (p) =>
        (projectName(p) || "").toLowerCase().includes(q) ||
        (clientName(p) || "").toLowerCase().includes(q)
    );
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          minWidth: 500,
          maxWidth: "min(864px, 90vw)",
          margin: "24px",
          maxHeight: "calc(100vh - 48px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ color: "#1773bc", fontWeight: 600, pb: 0 }}>
        Edit SOW
      </DialogTitle>
      <Divider sx={{ borderColor: "#1773bc", opacity: 0.5 }} />
      <DialogContent sx={{ pt: 2, overflow: "auto", flex: 1 }}>
        {loadingSOW ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>Loading SOW...</Box>
        ) : formData && sow ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Section 1: SOW Information */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1773bc", mb: 2 }}>
                  Section 1: SOW Information
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={formData.clientId}
                      label="Client"
                      onChange={(e) => handleSOWChange("clientId", e.target.value)}
                    >
                      <MenuItem value=""><em>Select Client</em></MenuItem>
                      {clients.map((c) => (
                        <MenuItem key={clientId(c)} value={clientId(c)}>{clientLabel(c)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField fullWidth label="SOW Name" value={formData.sowName} onChange={(e) => handleSOWChange("sowName", e.target.value)} required size="small" />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <DatePicker
                        label="Start Date"
                        value={formData.sowStartDate}
                        onChange={(v) => handleSOWChange("sowStartDate", v)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                      <DatePicker
                        label="End Date"
                        value={formData.sowEndDate}
                        onChange={(v) => handleSOWChange("sowEndDate", v)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Box>
                  </LocalizationProvider>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>SOW Type</InputLabel>
                      <Select value={formData.sowType} label="SOW Type" onChange={(e) => handleSOWChange("sowType", e.target.value)}>
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {sowTypeOptions.map((opt) => (
                          <MenuItem key={itemId(opt)} value={shortCode(opt)}>{shortCode(opt)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={formData.status} label="Status" onChange={(e) => handleSOWChange("status", e.target.value)}>
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {statusOptions.map((opt) => (
                          <MenuItem key={itemId(opt)} value={longCode(opt)}>{longCode(opt)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={employees}
                      getOptionLabel={(e) => empLabel(e) || ""}
                      isOptionEqualToValue={(a, b) => empId(a) === empId(b)}
                      value={employees.find((e) => empId(e) === formData.deliveryManagerUserId) ?? null}
                      onChange={(_, v) => handleSOWChange("deliveryManagerUserId", v ? empId(v) : "")}
                      renderInput={(params) => <TextField {...params} label="Delivery Manager" placeholder="Search..." />}
                    />
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={employees}
                      getOptionLabel={(e) => empLabel(e) || ""}
                      isOptionEqualToValue={(a, b) => empId(a) === empId(b)}
                      value={employees.find((e) => empId(e) === formData.accountManagerUserID) ?? null}
                      onChange={(_, v) => handleSOWChange("accountManagerUserID", v ? empId(v) : "")}
                      renderInput={(params) => <TextField {...params} label="Account Manager" placeholder="Search..." />}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Resource Count"
                    type="number"
                    value={formData.resourceCount ?? ""}
                    onChange={(e) => handleSOWChange("resourceCount", e.target.value)}
                    size="small"
                    inputProps={{ step: "0.01", min: "0" }}
                    placeholder="e.g. 5.5"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={!!formData.namedSOWFlag} onChange={(e) => handleSOWChange("namedSOWFlag", e.target.checked)} color="primary" />}
                    label="Named SOW"
                  />
                  <TextField fullWidth label="Note" value={formData.note || ""} onChange={(e) => handleSOWChange("note", e.target.value)} multiline rows={2} size="small" />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ backgroundColor: "#ed7d31" }}
                      onClick={() => setMonthlyProjectionOpen(true)}
                    >
                      Add Monthly Projection
                    </Button>
                    <Button variant="contained" size="small" sx={{ backgroundColor: "#1773bc" }} onClick={handleUpdateSOWInfo} disabled={updatingSOW}>
                      {updatingSOW ? "Updating..." : "Update SOW Info"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Section 2: Linked Projects */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1773bc", mb: 2 }}>
                  Section 2: Linked Projects
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={allProjects}
                  value={selectedProjects}
                  onChange={(_, v) => setSelectedProjects(v)}
                  getOptionLabel={(p) => projectLabel(p)}
                  isOptionEqualToValue={(a, b) => projectId(a) === projectId(b)}
                  filterOptions={filterProjectOptions}
                  loading={loadingLinked}
                  renderInput={(params) => <TextField {...params} label="Projects" placeholder="Search by project or client..." />}
                  renderTags={(value, getTagProps) => value.map((opt, i) => <Chip key={projectId(opt)} label={projectLabel(opt)} size="small" {...getTagProps({ index: i })} />)}
                  renderOption={(props, option) => <li {...props} key={projectId(option)}>{projectLabel(option)}</li>}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Button variant="contained" size="small" sx={{ backgroundColor: "#1773bc" }} onClick={handleUpdateLinkedProjects} disabled={updatingLinks || selectedProjects.length === 0}>
                    {updatingLinks ? "Updating..." : "Update Linked Projects"}
                  </Button>
                </Box>
              </CardContent>
            </Card>

          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>
      <SOWResourcingDetailsModal
        open={monthlyProjectionOpen}
        onClose={() => setMonthlyProjectionOpen(false)}
        sowId={sowId}
        sowName={formData?.sowName ?? sow?.sowName ?? sow?.SOWName}
        sowStartDate={formData?.sowStartDate ?? sow?.sowStartDate ?? sow?.SOWStartDate}
        sowEndDate={formData?.sowEndDate ?? sow?.sowEndDate ?? sow?.SOWEndDate}
        onSuccess={() => dispatch(getSOWData({ pageNo: 1, pageSize: 10, sortBy: "sowName", sortDir: "ASC", searchData: "", clientId: sowListClientFilterId }))}
      />
    </Dialog>
  );
};

export default SOWEditModal;
